import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

interface HeaderRow {
  key: string;
  value: string;
}

interface McpHeadersModalProps {
  open: boolean;
  mode: 'install' | 'edit';
  mcpName: string;
  /** Whether the user already has headers stored (edit mode). Values are never returned, only presence. */
  hasExistingHeaders?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (headers: Record<string, string>) => void;
}

type HeadersFormProps = Omit<McpHeadersModalProps, 'open' | 'mcpName'>;

const EMPTY_ROW: HeaderRow = { key: '', value: '' };

/**
 * Row editor. It lives inside `DialogContent`, which Radix unmounts while closed, so each
 * reopen starts from a blank row without any reset wiring.
 */
const HeadersForm = ({
  mode,
  hasExistingHeaders,
  isSubmitting,
  onClose,
  onSubmit,
}: HeadersFormProps) => {
  const { t } = useTranslation('admin');

  const [rows, setRows] = useState<HeaderRow[]>([EMPTY_ROW]);

  const updateRow = (index: number, patch: Partial<HeaderRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW]);

  const removeRow = (index: number) => {
    setRows((prev) => (prev.length === 1 ? [EMPTY_ROW] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = () => {
    const headers = rows.reduce<Record<string, string>>((acc, { key, value }) => {
      const trimmedKey = key.trim();
      if (trimmedKey) acc[trimmedKey] = value;
      return acc;
    }, {});
    onSubmit(headers);
  };

  // A static-headers MCP must carry at least one complete header — an empty install/save would
  // leave it effectively unauthenticated. (no_auth MCPs use the one-click install, not this modal.)
  const hasHeader = rows.some((row) => row.key.trim() !== '' && row.value.trim() !== '');

  return (
    <>
      <DialogBody className="flex flex-col gap-4 pt-4">
        <p className="text-body-md text-muted-foreground">
          <Trans
            i18nKey="userMcpServers.mcpHeadersModal.addTheAuthHeadersThis"
            ns="admin"
            components={{ auth: <code />, bearer: <code /> }}
          />
        </p>
        {mode === 'edit' && hasExistingHeaders && (
          <p className="text-body-sm text-muted-foreground">
            {t('userMcpServers.mcpHeadersModal.headersAreAlreadySetBut')}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {rows.map((row, index) => (
            <div key={index} className="flex items-end gap-2">
              <Field className="flex-1">
                <FieldLabel htmlFor={`mcp-header-${index}`}>
                  {t('userMcpServers.mcpHeadersModal.header')}
                </FieldLabel>
                <Input
                  id={`mcp-header-${index}`}
                  placeholder={t('userMcpServers.mcpHeadersModal.authorization')}
                  value={row.key}
                  onChange={(e) => updateRow(index, { key: e.target.value })}
                />
              </Field>
              <Field className="flex-1">
                <FieldLabel htmlFor={`mcp-value-${index}`}>
                  {t('userMcpServers.mcpHeadersModal.value')}
                </FieldLabel>
                <Input
                  id={`mcp-value-${index}`}
                  placeholder={t('userMcpServers.mcpHeadersModal.bearer')}
                  value={row.value}
                  onChange={(e) => updateRow(index, { value: e.target.value })}
                />
              </Field>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('userMcpServers.mcpHeadersModal.removeHeader')}
                onClick={() => removeRow(index)}
              >
                <Trash2Icon />
              </Button>
            </div>
          ))}
        </div>

        <div>
          <Button type="button" variant="ghost" onClick={addRow}>
            <PlusIcon />
            {t('userMcpServers.mcpHeadersModal.addHeader')}
          </Button>
        </div>
      </DialogBody>

      <DialogFooter className="pt-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          {t('userMcpServers.mcpHeadersModal.cancel')}
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!hasHeader || isSubmitting}>
          {isSubmitting && <Spinner aria-hidden />}
          {mode === 'install'
            ? t('userMcpServers.mcpHeadersModal.install')
            : t('userMcpServers.mcpHeadersModal.saveHeaders')}
        </Button>
      </DialogFooter>
    </>
  );
};

/**
 * Collects static auth headers for installing / re-crediting an MCP. Headers are write-only —
 * the API never returns stored values, so edit mode starts blank and submitting replaces them.
 * At least one complete header is required (static-headers MCPs only — no_auth uses one-click
 * install and never opens this); the backend rejects an empty static-headers install too.
 */
export const McpHeadersModal = ({ open, mcpName, onClose, ...formProps }: McpHeadersModalProps) => {
  const { t } = useTranslation('admin');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-w-160 gap-0 p-4">
        <DialogHeader>
          <DialogTitle>
            {formProps.mode === 'install'
              ? t('userMcpServers.mcpHeadersModal.installMcp', { mcpName })
              : t('userMcpServers.mcpHeadersModal.editHeadersFor', { mcpName })}
          </DialogTitle>
        </DialogHeader>
        <HeadersForm {...formProps} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};
